/**
 * Copyright 2026 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * JIRA CSV Export Utilities
 * 
 * Provides functions to generate CSV files formatted for JIRA import.
 * Supports exporting user stories and epics with proper field mapping.
 */

import {
  AcceptanceCriterion,
  normalizeAcceptanceCriteria,
  formatAcceptanceCriterion,
} from '@shared/types/user-stories';

export interface CsvUserStory {
  id: string;
  title: string;
  role?: string;
  goal?: string;
  benefit?: string;
  /**
   * Canonical acceptance-criterion shape - always `AcceptanceCriterion[]` internally.
   * External input (raw CSV parsing, legacy string arrays) must be run through
   * {@link normalizeAcceptanceCriteria} at the boundary before being assigned here.
   */
  acceptanceCriteria?: AcceptanceCriterion[];
  status?: string;
  epicId?: string;
}

export interface CsvEpic {
  id: string;
  title: string;
  description?: string;
  status?: string;
}

export interface JiraCsvOptions {
  projectKey?: string;
  epicIssueType?: string;
  storyIssueType?: string;
  filenamePrefix?: string;
  includeLabels?: boolean;
  /** Use markdown-style bullets (-) for JIRA Cloud, or wiki-style (*) for JIRA Server/DC */
  bulletStyle?: 'markdown' | 'wiki';
  /** Replace em-dashes and en-dashes with ASCII hyphens for older JIRA instances */
  normalizeUnicode?: boolean;
  /** Default priority for all issues (e.g., 'Medium', 'High') */
  defaultPriority?: string;
  /** Include Priority column in output */
  includePriority?: boolean;
}

const DEFAULT_OPTIONS: Required<JiraCsvOptions> = {
  projectKey: 'PROJ',
  epicIssueType: 'Epic',
  storyIssueType: 'Story',
  filenamePrefix: 'velocity-export',
  includeLabels: true,
  bulletStyle: 'markdown',
  normalizeUnicode: false,
  defaultPriority: 'Medium',
  includePriority: false,
};

/**
 * Escape a value for CSV (handles quotes, commas, newlines)
 */
export function escapeCsvValue(value: string): string {
  if (!value) return '';
  const needsQuotes = value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r');
  if (needsQuotes) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Determine the correct indefinite article ("a" or "an") for a word.
 * Uses "an" before vowel sounds.
 */
function getIndefiniteArticle(word: string): 'a' | 'an' {
  if (!word) return 'a';
  const firstChar = word.trim().toLowerCase()[0];
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  return vowels.includes(firstChar) ? 'an' : 'a';
}

/**
 * Normalize Unicode characters to ASCII equivalents.
 * Replaces em-dashes, en-dashes, smart quotes, etc.
 */
function normalizeUnicodeChars(text: string): string {
  return text
    .replace(/—/g, '--')  // em-dash to double hyphen
    .replace(/–/g, '-')   // en-dash to hyphen
    .replace(/'/g, "'")   // smart single quote (left)
    .replace(/'/g, "'")   // smart single quote (right)
    .replace(/"/g, '"')   // smart double quote (left)
    .replace(/"/g, '"')   // smart double quote (right)
    .replace(/…/g, '...') // ellipsis
    .replace(/•/g, '*');  // bullet point
}

interface FormatOptions {
  bulletStyle: 'markdown' | 'wiki';
  normalizeUnicode: boolean;
}

/**
 * Format a user story description for JIRA CSV import.
 * Uses the standard "As a... I want to... So that..." format.
 * Automatically selects correct article (a/an) based on the role.
 */
export function formatStoryDescription(
  story: CsvUserStory,
  options: FormatOptions = { bulletStyle: 'markdown', normalizeUnicode: false }
): string {
  const role = story.role || 'user';
  const article = getIndefiniteArticle(role);
  const bullet = options.bulletStyle === 'markdown' ? '-' : '*';
  
  const parts: string[] = [];
  
  parts.push(`As ${article} ${role},`);
  parts.push(`I want to ${story.goal || story.title},`);
  parts.push(`So that ${story.benefit || 'I can achieve my goals'}.`);
  parts.push('');
  
  if (story.acceptanceCriteria && story.acceptanceCriteria.length > 0) {
    const headerMark = options.bulletStyle === 'markdown' ? '**' : '*';
    parts.push(`${headerMark}Acceptance Criteria:${headerMark}`);
    story.acceptanceCriteria.forEach(criterion => {
      parts.push(`${bullet} ${formatAcceptanceCriterion(criterion)}`);
    });
  }
  
  let result = parts.join('\n');
  
  if (options.normalizeUnicode) {
    result = normalizeUnicodeChars(result);
  }
  
  return result;
}

/**
 * Format an epic description for JIRA CSV import
 */
export function formatEpicDescription(
  epic: CsvEpic, 
  storyCount: number,
  options: { normalizeUnicode: boolean } = { normalizeUnicode: false }
): string {
  const parts: string[] = [];
  
  if (epic.description) {
    parts.push(epic.description);
    parts.push('');
  }
  
  parts.push(`_Contains ${storyCount} user ${storyCount === 1 ? 'story' : 'stories'}_`);
  
  let result = parts.join('\n');
  
  if (options.normalizeUnicode) {
    result = normalizeUnicodeChars(result);
  }
  
  return result;
}

/**
 * Map Velocity status to JIRA-compatible status
 */
export function mapStatusToJira(status?: string): string {
  const statusMap: Record<string, string> = {
    'Not Started': 'To Do',
    'In Progress': 'In Progress',
    'Ready For Review': 'In Review',
    'In Review': 'In Review',
    'Completed': 'Done',
    'Blocked': 'Blocked',
    'On Hold': 'On Hold',
    'Groomed': 'To Do',
  };
  return statusMap[status || 'Not Started'] || 'To Do';
}

/**
 * Generate CSV content for JIRA import from epics and user stories.
 * 
 * @param epics - Array of epics with their story counts
 * @param stories - Array of stories with optional epic title for linking
 * @param options - Configuration options for the CSV output
 * @returns CSV content as a string
 */
export function generateJiraCsv(
  epics: Array<{ epic: CsvEpic; storyCount: number }>,
  stories: Array<{ story: CsvUserStory; epicTitle?: string }>,
  options: JiraCsvOptions = {}
): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const formatOpts: FormatOptions = {
    bulletStyle: opts.bulletStyle,
    normalizeUnicode: opts.normalizeUnicode,
  };
  
  const headers = [
    'Summary',
    'Issue Type',
    'Description',
    'Status',
    'Epic Name',
  ];
  
  if (opts.includePriority) {
    headers.push('Priority');
  }
  
  if (opts.includeLabels) {
    headers.push('Labels');
  }
  
  const rows: string[][] = [];
  
  // Add epics first
  for (const { epic, storyCount } of epics) {
    const row = [
      `${epic.id}: ${epic.title}`,
      opts.epicIssueType,
      formatEpicDescription(epic, storyCount, { normalizeUnicode: opts.normalizeUnicode }),
      mapStatusToJira(epic.status),
      '', // Epic Name is empty for epics themselves
    ];
    if (opts.includePriority) {
      row.push(opts.defaultPriority);
    }
    if (opts.includeLabels) {
      row.push(`velocity-epic,velocity-${epic.id}`);
    }
    rows.push(row);
  }
  
  // Add stories
  for (const { story, epicTitle } of stories) {
    const row = [
      `${story.id}: ${story.title}`,
      opts.storyIssueType,
      formatStoryDescription(story, formatOpts),
      mapStatusToJira(story.status),
      epicTitle || '', // Epic Name for parent linking
    ];
    if (opts.includePriority) {
      row.push(opts.defaultPriority);
    }
    if (opts.includeLabels) {
      row.push(`velocity-story,velocity-${story.id}`);
    }
    rows.push(row);
  }
  
  // Build CSV content
  const csvContent = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map(row => row.map(escapeCsvValue).join(','))
  ].join('\n');
  
  return csvContent;
}

/**
 * Generate a timestamped filename for the CSV export
 */
export function generateCsvFilename(prefix: string = 'velocity-export'): string {
  const timestamp = new Date().toISOString().split('T')[0];
  return `${prefix}-${timestamp}.csv`;
}

/**
 * Trigger a file download in the browser
 */
export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Convenience function to generate and download a JIRA CSV file
 */
export function exportToJiraCsv(
  epics: Array<{ epic: CsvEpic; storyCount: number }>,
  stories: Array<{ story: CsvUserStory; epicTitle?: string }>,
  options: JiraCsvOptions = {}
): { filename: string; epicCount: number; storyCount: number } {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const csvContent = generateJiraCsv(epics, stories, opts);
  const filename = generateCsvFilename(opts.filenamePrefix);
  
  downloadCsv(csvContent, filename);
  
  return {
    filename,
    epicCount: epics.length,
    storyCount: stories.length,
  };
}

// ============================================================================
// CSV Import Functions
// ============================================================================

export interface CsvImportResult {
  epics: CsvEpic[];
  stories: CsvUserStory[];
  errors: string[];
  warnings: string[];
}

export interface CsvRow {
  Summary: string;
  'Issue Type': string;
  Description: string;
  Status: string;
  'Epic Name': string;
  Labels?: string;
  Priority?: string;
  [key: string]: string | undefined;
}

/**
 * Parse CSV content into rows.
 * Handles multiline quoted values correctly.
 */
export function parseCsvContent(content: string): { headers: string[]; rows: string[][] } {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const nextChar = content[i + 1];
    
    if (char === '"') {
      if (!inQuotes) {
        inQuotes = true;
      } else if (nextChar === '"') {
        currentField += '"';
        i++; // Skip the escaped quote
      } else {
        inQuotes = false;
      }
    } else if (char === ',' && !inQuotes) {
      currentRow.push(currentField);
      currentField = '';
    } else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
      // End of row (handle both \n and \r\n)
      if (char === '\r') i++; // Skip the \n in \r\n
      currentRow.push(currentField);
      if (currentRow.some(field => field.trim())) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentField = '';
    } else {
      currentField += char;
    }
  }
  
  // Don't forget the last row and field
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField);
    if (currentRow.some(field => field.trim())) {
      rows.push(currentRow);
    }
  }
  
  const headers = rows.shift() || [];
  return { headers, rows };
}

/**
 * Convert CSV rows to objects using headers
 */
export function csvRowsToObjects(headers: string[], rows: string[][]): CsvRow[] {
  return rows.map(row => {
    const obj: CsvRow = {
      Summary: '',
      'Issue Type': '',
      Description: '',
      Status: '',
      'Epic Name': '',
    };
    headers.forEach((header, index) => {
      obj[header] = row[index] || '';
    });
    return obj;
  });
}

/**
 * Parse user story description to extract role, goal, benefit, and acceptance criteria.
 * Handles both markdown and wiki formats.
 */
export function parseStoryDescription(description: string): {
  role: string;
  goal: string;
  benefit: string;
  acceptanceCriteria: string[];
} {
  const result = {
    role: '',
    goal: '',
    benefit: '',
    acceptanceCriteria: [] as string[],
  };
  
  if (!description) return result;
  
  const lines = description.split('\n');
  let inAcceptanceCriteria = false;
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    // Check for "As a/an ..." pattern
    const asMatch = trimmed.match(/^As\s+(?:a|an)\s+(.+?),?\s*$/i);
    if (asMatch) {
      result.role = asMatch[1].trim();
      continue;
    }
    
    // Check for "I want to ..." pattern
    const wantMatch = trimmed.match(/^I\s+want\s+to\s+(.+?),?\s*$/i);
    if (wantMatch) {
      result.goal = wantMatch[1].trim();
      continue;
    }
    
    // Check for "So that ..." pattern
    const soThatMatch = trimmed.match(/^So\s+that\s+(.+?)\.?\s*$/i);
    if (soThatMatch) {
      result.benefit = soThatMatch[1].trim();
      continue;
    }
    
    // Check for acceptance criteria header (markdown or wiki style)
    if (trimmed.match(/^\*?\*?Acceptance\s+Criteria:?\*?\*?$/i)) {
      inAcceptanceCriteria = true;
      continue;
    }
    
    // Parse acceptance criteria bullets (- or *)
    if (inAcceptanceCriteria) {
      const bulletMatch = trimmed.match(/^[-*]\s+(.+)$/);
      if (bulletMatch) {
        result.acceptanceCriteria.push(bulletMatch[1].trim());
      }
    }
  }
  
  return result;
}

/**
 * Parse summary to extract ID and title.
 * Expected formats:
 * - Story ID: "ADM-PLT-001: Create Organization Account" (XXX-XXX-NNN)
 * - Epic ID: "ANA-RPT: Analytics & Reporting" (XXX-XXX)
 * - Issue ID: "BUG-001: Fix login issue" (XXX-NNN)
 */
export function parseSummary(summary: string): { id: string; title: string } {
  // Match story IDs (XXX-XXX-NNN), epic IDs (XXX-XXX), or issue IDs (XXX-NNN)
  const match = summary.match(/^([A-Z]+-[A-Z]+-\d+|[A-Z]+-[A-Z]+|[A-Z]+-\d+):\s*(.+)$/);
  if (match) {
    return { id: match[1], title: match[2].trim() };
  }
  // If no ID pattern found, generate one and use whole summary as title
  return { id: '', title: summary };
}

/**
 * Map JIRA status to Velocity status
 */
export function mapJiraStatusToVelocity(jiraStatus: string): string {
  const statusMap: Record<string, string> = {
    'To Do': 'Not Started',
    'In Progress': 'In Progress',
    'In Review': 'In Review',
    'Done': 'Completed',
    'Blocked': 'Blocked',
    'On Hold': 'On Hold',
  };
  return statusMap[jiraStatus] || jiraStatus || 'Not Started';
}

/**
 * Import CSV content and convert to epics and user stories.
 * Supports the same format as the JIRA CSV export.
 */
export function importFromCsv(content: string): CsvImportResult {
  const result: CsvImportResult = {
    epics: [],
    stories: [],
    errors: [],
    warnings: [],
  };
  
  try {
    const { headers, rows } = parseCsvContent(content);
    
    // Validate required headers
    const requiredHeaders = ['Summary', 'Issue Type', 'Description'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      result.errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
      return result;
    }
    
    const csvRows = csvRowsToObjects(headers, rows);
    
    // Build epic map for linking stories
    const epicMap = new Map<string, CsvEpic>();
    
    // First pass: collect epics
    for (let i = 0; i < csvRows.length; i++) {
      const row = csvRows[i];
      const issueType = row['Issue Type']?.toLowerCase() || '';
      
      if (issueType === 'epic') {
        const { id, title } = parseSummary(row.Summary);
        
        if (!id) {
          result.warnings.push(`Row ${i + 2}: Epic missing ID in Summary, generating one`);
        }
        
        const epicId = id || `EPIC-${String(result.epics.length + 1).padStart(3, '0')}`;
        const epic: CsvEpic = {
          id: epicId,
          title: title,
          description: row.Description || '',
          status: mapJiraStatusToVelocity(row.Status || ''),
        };
        
        result.epics.push(epic);
        // Map both the full summary and ID for lookups
        epicMap.set(`${epicId}: ${title}`, epic);
        epicMap.set(epicId, epic);
      }
    }
    
    // Second pass: collect stories
    for (let i = 0; i < csvRows.length; i++) {
      const row = csvRows[i];
      const issueType = row['Issue Type']?.toLowerCase() || '';
      
      if (issueType === 'story' || issueType === 'user story') {
        const { id, title } = parseSummary(row.Summary);
        const parsed = parseStoryDescription(row.Description || '');
        
        if (!id) {
          result.warnings.push(`Row ${i + 2}: Story missing ID in Summary, generating one`);
        }
        
        const storyId = id || `STORY-${String(result.stories.length + 1).padStart(3, '0')}`;
        
        // Find epic ID from Epic Name column
        let epicId: string | undefined;
        const epicName = row['Epic Name'];
        if (epicName) {
          const epic = epicMap.get(epicName);
          if (epic) {
            epicId = epic.id;
          } else {
            // Try to extract epic ID from the name
            const epicIdMatch = epicName.match(/^([A-Z]+-[A-Z]+|[A-Z]+-\d+):/);
            if (epicIdMatch) {
              epicId = epicIdMatch[1];
            }
          }
        }
        
        const story: CsvUserStory = {
          id: storyId,
          title: title,
          role: parsed.role || 'user',
          goal: parsed.goal || title,
          benefit: parsed.benefit || '',
          // Boundary conversion: CSV bullets come out as strings; upgrade them to the
          // canonical AcceptanceCriterion shape here so no caller downstream has to
          // worry about the union.
          acceptanceCriteria: normalizeAcceptanceCriteria(parsed.acceptanceCriteria),
          status: mapJiraStatusToVelocity(row.Status || ''),
          epicId: epicId,
        };
        
        result.stories.push(story);
      }
    }
    
    // Validate: no items found
    if (result.epics.length === 0 && result.stories.length === 0) {
      result.errors.push('No epics or user stories found in CSV. Ensure Issue Type column contains "Epic" or "Story" values.');
    }
    
  } catch (error) {
    result.errors.push(`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
  
  return result;
}

/**
 * Read a file and return its contents as text
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}
